import config from 'constants/config'
import GeoContext from 'contexts/geoContext'
import { isTakeoverId } from 'lib/lab/dynamicPayload'
import moment from 'moment'
import { useContext, useMemo } from 'react'
import { takeovers } from 'takeovers/takeoverRegistry'
import { useSiteTakeoverTheme } from './useSiteTakeoverTheme'

// Union type for all supported offer types in takeovers
type SupportedOfferTypes =
  | App.Offer
  | App.OfferSummary
  | App.HotelOffer
  | App.HotelOfferSummary
  | App.Tours.TourV2Offer
  | App.Tours.TourV2OfferSummary
  | App.CruiseOffer
  | App.VillaOffer
  | App.VillaOfferSummary
  | App.TourOffer
  | App.TourOfferSummary
  | App.ExperienceOffer

interface TakeoverOptions {
  /**
   * Check which takeover applies to the vertical given
   */
  vertical?: App.SiteTakeoverVertical;
  /**
   * Check which takeover applies to the offer given
   */
  offer?: SupportedOfferTypes;
  /**
   * Check which takeover applies for specifically the hero carousel condition
   */
  heroVertical?: App.SiteTakeoverHeroVertical;
}

/**
 * Determines if a takeover is active in a specific region based on the takeover's region configuration.
 * Supports both inclusive (whitelist) and exclusive (blacklist) region targeting.
 *
 * @param takeover - The site takeover configuration containing region rules
 * @param regionCode - The current user's region code to check against
 * @returns True if the takeover should be active in the given region, false otherwise
 */
function isTakeoverActiveInRegion(takeover: App.SiteTakeoverConfig, regionCode: string): boolean {
  switch (takeover.regions.type) {
    case 'inclusive':
      return takeover.regions.list.has(regionCode)
    case 'exclusive':
      return !takeover.regions.list.has(regionCode)
  }
}

/**
 * Type guard to check if an offer has the 'type' property needed for offer type filtering.
 * Validates that the offer contains the 'type' property required for matching against takeover offer types.
 *
 * @param offer - The offer object to validate, can be any of the supported offer types
 * @returns True if the offer has the type property, false otherwise
 */
function isOfferWithType(offer: SupportedOfferTypes): offer is SupportedOfferTypes & { type: App.OfferType } {
  return 'type' in offer && typeof offer.type === 'string'
}

/**
 * Type guard to check if an offer has the 'campaignTags' property needed for campaign tag filtering.
 * Validates that the offer contains the 'campaignTags' property required for matching against takeover campaign tags.
 *
 * @param offer - The offer object to validate, can be any of the supported offer types
 * @returns True if the offer has the campaignTags property as an array, false otherwise
 */
function isOfferWithCampaignTags(offer: SupportedOfferTypes): offer is SupportedOfferTypes & { campaignTags: Array<string> } {
  return 'campaignTags' in offer && Array.isArray(offer.campaignTags)
}

/**
 * Determines if a takeover is applicable based on the provided filtering options.
 * Checks vertical targeting, hero vertical targeting, offer type matching, and campaign tag matching.
 * All conditions must be satisfied for the takeover to be considered applicable.
 *
 * @param takeover - The site takeover configuration to check applicability for
 * @param options - Filtering options to determine applicability
 * @param options.vertical - Optional vertical filter (e.g., 'flights', 'hotels')
 * @param options.offer - Optional offer to match against takeover's offer type and campaign tags
 * @param options.heroVertical - Optional hero vertical filter for hero carousel sections
 * @returns True if the takeover matches all provided filtering criteria, false otherwise
 *
 * @example
 * ```tsx
 * // Check if takeover applies to flights vertical
 * const isApplicable = isTakeoverApplicable(takeover, { vertical: 'flights' })
 *
 * // Check if takeover applies to a specific offer
 * const isOfferApplicable = isTakeoverApplicable(takeover, { offer: myOffer })
 *
 * // Check multiple criteria
 * const isFullyApplicable = isTakeoverApplicable(takeover, {
 *   vertical: 'hotels',
 *   heroVertical: 'hotels',
 *   offer: hotelOffer
 * })
 * ```
 */
export function isTakeoverApplicable(
  takeover: App.SiteTakeoverConfig | App.SiteTakeoverConfigV2,
  options: TakeoverOptions
): boolean {
  const { vertical, offer, heroVertical } = options

  // Is the takeover for this particular vertical?
  const isVerticalApplicable = vertical ? takeover.verticals.has(vertical) : true

  // Is the takeover for this particular hero vertical? Fallback to upper level vertical if not specified
  const isHeroVerticalApplicable = heroVertical ? takeover.heroSection.verticals?.has(heroVertical) : true

  // Is the takeover for this particular offer type?
  let isOfferTypeApplicable = true
  if (offer && takeover.offerTypes) {
    isOfferTypeApplicable = isOfferWithType(offer) && takeover.offerTypes.has(offer.type)
  }

  // Is the takeover for this particular campaign tag?
  let isCampaignTagApplicable = true
  if (offer && takeover.campaignTags) {
    isCampaignTagApplicable =
      isOfferWithCampaignTags(offer) &&
      offer.campaignTags.some(tag => takeover.campaignTags?.has(tag))
  }

  return isVerticalApplicable &&
    isHeroVerticalApplicable &&
    isOfferTypeApplicable &&
    isCampaignTagApplicable
}

/**
 * Hook to get the current active site takeover configuration (V2 implementation).
 * This version uses lab feature flags to determine which takeover to display,
 * allowing for controlled rollouts and A/B testing of takeovers.
 *
 * @param options - Configuration options for filtering takeovers by vertical, offer, or hero vertical
 * @param options.vertical - Filter takeovers by specific vertical (e.g., 'flights', 'hotels')
 * @param options.offer - Filter takeovers by specific offer properties (type, campaign tags)
 * @param options.heroVertical - Filter takeovers for hero carousel sections by vertical
 * @returns The active takeover configuration if applicable, undefined otherwise
 *
 * @example
 * ```tsx
 * // Get any active takeover
 * const takeover = useCurrentSiteTakeoverV2()
 *
 * // Get takeover specific to flights vertical
 * const flightsTakeover = useCurrentSiteTakeoverV2({ vertical: 'flights' })
 *
 * // Get takeover for a specific offer
 * const offerTakeover = useCurrentSiteTakeoverV2({ offer: myOffer })
 * ```
 */
export function useCurrentSiteTakeoverV2(options: TakeoverOptions = {}): App.SiteTakeoverConfigV2 | undefined {
  const takeoverTheme = useSiteTakeoverTheme()
  if (!takeoverTheme) {
    return undefined
  }

  const takeoverId = takeoverTheme.id
  if (!isTakeoverId(takeoverId)) {
    return undefined
  }
  const takeover = takeovers[takeoverId]

  return isTakeoverApplicable(takeover, options) ? takeover : undefined
}

/**
 * Hook to get the current active site takeover configuration (Legacy implementation).
 * This version uses date-based activation and region targeting to determine which takeover to display.
 * Supports debug mode via window.__DEBUG_TAKEOVER_ID__ for testing specific takeovers.
 *
 * @param options - Configuration options for filtering takeovers by vertical, offer, or hero vertical
 * @param options.vertical - Filter takeovers by specific vertical (e.g., 'flights', 'hotels')
 * @param options.offer - Filter takeovers by specific offer properties (type, campaign tags)
 * @param options.heroVertical - Filter takeovers for hero carousel sections by vertical
 * @returns The active takeover configuration if applicable, undefined otherwise
 *
 * @example
 * ```tsx
 * // Get any active takeover
 * const takeover = useCurrentSiteTakeover()
 *
 * // Get takeover specific to hotels vertical
 * const hotelsTakeover = useCurrentSiteTakeover({ vertical: 'hotels' })
 *
 * // Get takeover for hero carousel in tours vertical
 * const heroTakeover = useCurrentSiteTakeover({ heroVertical: 'tours' })
 * ```
 * @deprecated Use useCurrentSiteTakeoverV2 instead
 */
function useCurrentSiteTakeover(options: TakeoverOptions = {}): App.SiteTakeoverConfig | undefined {
  const { currentRegionCode } = useContext(GeoContext)
  const { vertical, offer, heroVertical } = options
  const currentTakeover = useMemo(() => {
    const now = moment()
    return Object.values(takeovers).find(takeover => {
      let active = false
      if (typeof window !== 'undefined' && window.__DEBUG_TAKEOVER_ID__) {
        const debugId = window.__DEBUG_TAKEOVER_ID__.toLowerCase()
        active = takeover.id.toLowerCase() === debugId
      } else {
        // first we see if the takeover is active by ensuring this is the right brand/region code and date
        active = takeover.brands.has(config.BRAND) &&
          isTakeoverActiveInRegion(takeover, currentRegionCode) &&
          now.isBetween(takeover.startDate, takeover.endDate)
      }

      // Next, if any options are provided - we ensure they match the takeover
      const applicable = isTakeoverApplicable(takeover, { vertical, offer, heroVertical })

      return active && applicable
    })
  }, [currentRegionCode, vertical, offer, heroVertical])

  return currentTakeover
}

export default useCurrentSiteTakeover
